import React, { useMemo, useEffect } from 'react';
import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
  alpha,
} from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import GlobalStyles from '@mui/material/GlobalStyles';
import { setThemeMode } from '../store/coreSlice';
import { getFilledButtonText, getThemeButtonColor } from './buttonContrast';
import { darkSurfaces, darkSurfaceCssVars } from './darkSurfaces';
import { tableHeaderTypography } from './GlassmorphismDesignSystem';

const isValidHex = (value) =>
  typeof value === 'string' && /^#[0-9A-Fa-f]{6}$/.test(value);

const CustomThemeProvider = ({ children }) => {
  const dispatch = useDispatch();
  const businessData = useSelector((state) => state.core.businessData);
  const userTheme = useSelector((state) => state.core.user?.theme);
  const themeModeRedux = useSelector((state) => state.core.themeMode);
  const themeMode = userTheme || themeModeRedux || 'light';

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      dispatch(setThemeMode(e.matches ? 'dark' : 'light'));
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [dispatch]);

  // Inject CSS custom properties for scrollbar theming
  useEffect(() => {
    const root = document.documentElement;
    const primaryColor = businessData?.mainColor || '#000000';
    const secondaryColor = businessData?.secColor || '#69696990';
    const idleColor = businessData?.idleColor || '#94A3B8';
    const successColor = businessData?.successColor || '#10B981';
    const errorColor = businessData?.errorColor || '#EF4444';

    // Set CSS custom properties
    root.style.setProperty('--primary-color', alpha(primaryColor, 0.15));
    root.style.setProperty(
      '--primary-color-light',
      alpha(primaryColor, 0.1)
    );
    root.style.setProperty(
      '--primary-color-dark',
      alpha(primaryColor, 0.25)
    );
    root.style.setProperty(
      '--secondary-color',
      alpha(secondaryColor, 0.15)
    );
    root.style.setProperty('--idle-color', alpha(idleColor, 0.15));
    root.style.setProperty('--success-color', alpha(successColor, 0.15));
    root.style.setProperty('--error-color', alpha(errorColor, 0.15));

    // Theme mode specific colors
    if (themeMode === 'dark') {
      root.setAttribute('data-mui-color-scheme', 'dark');
    } else {
      root.setAttribute('data-mui-color-scheme', 'light');
    }

    return () => {
      // Cleanup on unmount
      root.style.removeProperty('--primary-color');
      root.style.removeProperty('--primary-color-light');
      root.style.removeProperty('--primary-color-dark');
      root.style.removeProperty('--secondary-color');
      root.style.removeProperty('--idle-color');
      root.style.removeProperty('--success-color');
      root.style.removeProperty('--error-color');
    };
  }, [
    businessData?.mainColor,
    businessData?.secColor,
    businessData?.idleColor,
    businessData?.successColor,
    businessData?.errorColor,
    themeMode,
  ]);

  const theme = useMemo(() => {
  
    try {
      const isDark = themeMode === 'dark';
      const primaryColor = isValidHex(businessData?.mainColor)
        ? businessData.mainColor
        : '#1F2937';
      const secondaryColor = isValidHex(businessData?.secColor)
        ? businessData.secColor
        : '#64748B';
      const successColor = isValidHex(businessData?.successColor)
        ? businessData.successColor
        : '#10B981';
      const errorColor = isValidHex(businessData?.errorColor)
        ? businessData.errorColor
        : '#EF4444';
      const warningColor = isValidHex(businessData?.idleColor)
        ? businessData.idleColor
        : '#D97706';
      const primaryContrastText = getFilledButtonText(primaryColor);
      const surfaceBorder = isDark
        ? darkSurfaces.rim
        : 'rgba(15, 23, 42, 0.08)';
      const subtleBorder = isDark
        ? 'rgba(255, 255, 255, 0.06)'
        : 'rgba(15, 23, 42, 0.06)';
      const hoverSurface = isDark
        ? 'rgba(255, 255, 255, 0.07)'
        : 'rgba(15, 23, 42, 0.045)';
      const inputSurface = isDark
        ? darkSurfaces.inset
        : 'rgba(255, 255, 255, 0.72)';

      const themeConfig = {
        components: {
          MuiButtonBase: {
            styleOverrides: {
              root: {
                '&:focus-visible': {
                  outline: `2px solid ${alpha(primaryColor, 0.55)}`,
                  outlineOffset: 2,
                },
              },
            },
          },
          MuiButton: {
            defaultProps: {
              disableElevation: true,
            },
            styleOverrides: {
              root: {
                minHeight: '32px',
                borderRadius: '7px',
                padding: '6px 11px',
                fontSize: '0.8125rem',
                fontWeight: 600,
                lineHeight: 1.35,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: 'none',
                },
              },
              contained: ({ theme, ownerState }) => {
                const palette = theme.palette[ownerState.color || 'primary'];
                if (!palette?.main) return {};
                return {
                  color: getFilledButtonText(palette.main),
                  '&:hover': {
                    backgroundColor: palette.dark,
                    color: getFilledButtonText(palette.dark),
                    '@media (hover: none)': {
                      backgroundColor: palette.main,
                      color: getFilledButtonText(palette.main),
                    },
                  },
                };
              },
              outlined: ({ theme, ownerState }) => ({
                color: getThemeButtonColor(theme, ownerState.color),
                borderColor: ownerState.color === 'inherit' ? surfaceBorder : alpha(getThemeButtonColor(theme, ownerState.color), 0.4),
                '&:hover': {
                  borderColor: 'currentColor',
                },
              }),
              text: ({ theme, ownerState }) => ({
                color: getThemeButtonColor(theme, ownerState.color),
              }),
              sizeSmall: {
                minHeight: '28px',
                padding: '4px 9px',
                fontSize: '0.75rem',
              },
            },
          },
          MuiIconButton: {
            styleOverrides: {
              root: {
                padding: 6,
                borderRadius: 7,
                color: isDark ? '#D7DDE5' : '#526070',
                '&:hover': {
                  backgroundColor: hoverSurface,
                  color: isDark ? '#FFFFFF' : '#1F2937',
                },
              },
              sizeSmall: {
                padding: 5,
              },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                minHeight: '36px',
                borderRadius: '7px',
                backgroundColor: inputSurface,
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(primaryColor, 0.45),
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderWidth: 1,
                  borderColor: primaryColor,
                  boxShadow: `0 0 0 2px ${alpha(primaryColor, 0.12)}`,
                },
              },
              notchedOutline: {
                borderRadius: '7px',
                borderColor: surfaceBorder,
              },
              input: {
                padding: '8px 11px',
              },
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                '& .MuiInputLabel-root': {
                  fontSize: '0.8125rem',
                },
              },
            },
          },
          MuiInputLabel: {
            styleOverrides: {
              root: {
                color: isDark ? '#B3BBC6' : '#586474',
                '&.Mui-focused': {
                  color: primaryColor,
                },
              },
            },
          },
          MuiSelect: {
            styleOverrides: {
              select: {
                minHeight: 'unset',
                padding: '8px 32px 8px 11px',
              },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: {
                height: 24,
                borderRadius: 999,
                fontSize: '0.75rem',
                fontWeight: 600,
              },
              label: {
                paddingLeft: 8,
                paddingRight: 8,
              },
            },
          },
          MuiTooltip: {
            styleOverrides: {
              tooltip: {
                backgroundColor: isDark ? darkSurfaces.hover : '#1F2937',
                color: '#FFFFFF',
                fontSize: '0.75rem',
                lineHeight: 1.35,
                borderRadius: 6,
                padding: '6px 8px',
              },
              arrow: {
                color: isDark ? darkSurfaces.hover : '#1F2937',
              },
            },
          },
          MuiMenu: {
            styleOverrides: {
              paper: {
                marginTop: 4,
                border: `1px solid ${subtleBorder}`,
                borderRadius: 9,
                boxShadow: isDark
                  ? '0 8px 24px rgba(0, 0, 0, 0.28)'
                  : '0 8px 24px rgba(15, 23, 42, 0.12)',
                backgroundImage: 'none',
              },
              list: {
                padding: 4,
              },
            },
          },
          MuiMenuItem: {
            styleOverrides: {
              root: {
                minHeight: 32,
                borderRadius: 6,
                gap: 8,
                padding: '6px 9px',
                fontSize: '0.8125rem',
                '&:hover': {
                  backgroundColor: hoverSurface,
                },
                '&.Mui-selected': {
                  backgroundColor: alpha(primaryColor, 0.1),
                  '&:hover': {
                    backgroundColor: alpha(primaryColor, 0.14),
                  },
                },
              },
            },
          },
          MuiDialog: {
            styleOverrides: {
              paper: {
                border: `1px solid ${subtleBorder}`,
                borderRadius: 12,
                boxShadow: isDark
                  ? '0 16px 48px rgba(0, 0, 0, 0.4)'
                  : '0 16px 48px rgba(15, 23, 42, 0.16)',
                backgroundImage: 'none',
              },
            },
          },
          MuiTabs: {
            styleOverrides: {
              root: {
                minHeight: 36,
              },
              indicator: ({ theme, ownerState }) => ({
                height: 2,
                borderRadius: 2,
                backgroundColor: getThemeButtonColor(theme, ownerState.indicatorColor || 'primary'),
              }),
            },
          },
          MuiTab: {
            styleOverrides: {
              root: ({ theme, ownerState }) => ({
                minHeight: 36,
                minWidth: 0,
                padding: '7px 12px',
                fontSize: '0.8125rem',
                fontWeight: 600,
                textTransform: 'none',
                '&.Mui-selected:not(.Mui-disabled)': {
                  color: getThemeButtonColor(theme, ownerState.textColor || 'primary'),
                },
              }),
            },
          },
          MuiTableCell: {
            styleOverrides: {
              root: {
                padding: '8px 12px',
                borderBottom: `1px solid ${subtleBorder}`,
                fontSize: '0.8125rem',
              },
              head: {
                ...tableHeaderTypography,
                color: isDark ? '#B3BBC6' : '#586474',
                fontWeight: 700,
              },
            },
          },
          MuiTableRow: {
            styleOverrides: {
              root: {
                '&:hover': {
                  backgroundColor: hoverSurface,
                },
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 10,
                backgroundImage: 'none',
              },
            },
          },
          MuiPickersOutlinedInput: {
            styleOverrides: {
              root: {
                minHeight: '36px',
                borderRadius: '7px',
                height: '36px',
                backgroundColor: inputSurface,
              },
              notchedOutline: {
                borderRadius: '7px',
                borderColor: surfaceBorder,
              },
            },
          },
          MuiPickersTextField: {
            styleOverrides: {
              root: {
                borderRadius: '7px',
              },
            },
          },
        },
        palette: {
          mode: themeMode,
          primary: {
            main: primaryColor,
            contrastText: primaryContrastText,
          },
          secondary: {
            main: secondaryColor,
          },
          success: {
            main: successColor,
          },
          error: {
            main: errorColor,
          },
          warning: {
            main: warningColor,
          },
          info: {
            main: '#2563EB',
          },
          background: {
            default: isDark ? darkSurfaces.canvas : '#F7F8FA',
            paper: isDark ? darkSurfaces.panel : '#FFFFFF',
          },
          text: {
            primary: isDark ? '#F3F4F6' : '#1F2937',
            secondary: isDark ? '#B3BBC6' : '#586474',
          },
          action: {
            active:
              isDark ? 'rgba(255, 255, 255, 0.68)' : 'rgba(31, 41, 55, 0.7)',
            hover:
              hoverSurface,
            selected:
              isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(31, 41, 55, 0.09)',
            disabled:
              isDark ? 'rgba(255, 255, 255, 0.32)' : 'rgba(31, 41, 55, 0.38)',
            disabledBackground:
              isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(31, 41, 55, 0.08)',
          },
          divider:
            isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(15, 23, 42, 0.1)',
        },
        breakpoints: {
          values: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1280,
            xl: 1920,
          },
        },
        typography: {
          fontFamily: (businessData?.font && typeof businessData.font === 'string')
            ? businessData.font
            : '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          color: isDark ? '#F3F4F6' : '#1F2937',
          fontSize: 13,
          body1: {
            fontSize: '0.8125rem',
            lineHeight: 1.45,
          },
          body2: {
            fontSize: '0.75rem',
            lineHeight: 1.45,
          },
          caption: {
            fontSize: '0.6875rem',
            lineHeight: 1.35,
            letterSpacing: '0.01em',
          },
          h1: {
            fontSize: '1.75rem',
            fontWeight: 700,
            lineHeight: 1.2,
          },
          h2: {
            fontSize: '1.5rem',
            fontWeight: 700,
            lineHeight: 1.25,
          },
          h3: {
            fontSize: '1.25rem',
            fontWeight: 700,
            lineHeight: 1.3,
          },
          h4: {
            fontSize: '1.125rem',
            fontWeight: 700,
            lineHeight: 1.35,
          },
          h5: {
            fontSize: '1rem',
            fontWeight: 700,
            lineHeight: 1.35,
          },
        },
        shape: {
          borderRadius: 7,
        },
        transitions: {
          duration: {
            shortest: 150,
            shorter: 200,
            short: 250,
            standard: 300,
            complex: 375,
            enteringScreen: 225,
            leavingScreen: 195,
          },
          easing: {
            easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
            easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
            easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
            sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
          },
        },
      };
    
      return createTheme(themeConfig);
    } catch (error) {
      console.error('Error creating theme:', error);
      console.error('Error details:', error.stack);
      return createTheme({
        palette: { mode: themeMode || 'light' }
      });
    }
  }, [businessData, themeMode]);

  return (
    <MuiThemeProvider theme={theme}>
      <GlobalStyles
        styles={{
          // ThemeProvider alone does not style inherited text or native controls.
          html: { colorScheme: theme.palette.mode, ...darkSurfaceCssVars },
          body: {
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.background.default,
            '@media print': {
              color: theme.palette.common.black,
              backgroundColor: theme.palette.common.white,
            },
          },
        }}
      />
      {children}
    </MuiThemeProvider>
  );
};

CustomThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default CustomThemeProvider;
